import { useEffect, useId, useState } from "react";
import { create } from "zustand";
import {
  localSnoozeDate,
  localSnoozeTime,
  resolveCustomSnooze,
  type CustomSnoozeInput,
} from "../threadSnooze.logic";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Toggle, ToggleGroup } from "./ui/toggle-group";
import { Select, SelectTrigger, SelectValue, SelectPopup, SelectItem } from "./ui/select";
import {
  NumberField,
  NumberFieldGroup,
  NumberFieldInput,
  NumberFieldDecrement,
  NumberFieldIncrement,
} from "./ui/number-field";
import {
  Dialog,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogPanel,
  DialogFooter,
} from "./ui/dialog";

export interface SnoozeChoice {
  readonly until: string;
}

interface Request {
  readonly resolve: (choice: SnoozeChoice | null) => void;
}

const useRequest = create<{ request: Request | null }>(() => ({ request: null }));

/**
 * Ask the user for a wake time and resolve with their choice, or null if they
 * dismiss it. A promise rather than a component prop so a snooze menu anywhere
 * can offer "Custom…" without owning a dialog of its own; only one request can
 * be outstanding, and a second one cancels the first.
 */
export function requestCustomSnooze(): Promise<SnoozeChoice | null> {
  useRequest.getState().request?.resolve(null);
  return new Promise((resolve) => useRequest.setState({ request: { resolve } }));
}

function finish(choice: SnoozeChoice | null) {
  const request = useRequest.getState().request;
  useRequest.setState({ request: null });
  request?.resolve(choice);
}

/**
 * Mounted once by the app shell. Without it a "Custom…" menu item would await a
 * promise that nothing can ever settle.
 */
export function CustomSnoozeDialogHost() {
  const request = useRequest((state) => state.request);
  // An unmounting host still has to settle its caller, or the awaiting menu
  // handler is left pending for the life of the page.
  useEffect(() => () => finish(null), []);
  return request ? <CustomSnoozeDialog /> : null;
}

function CustomSnoozeDialog() {
  const id = useId();
  const [initial] = useState(() => new Date(Date.now() + 3_600_000));
  const [mode, setMode] = useState<CustomSnoozeInput["mode"]>("date");
  const [date, setDate] = useState(() => localSnoozeDate(initial));
  const [time, setTime] = useState(() => localSnoozeTime(initial));
  const [amount, setAmount] = useState("2");
  const [unit, setUnit] = useState<"minutes" | "hours" | "days">("hours");
  const [error, setError] = useState<string | null>(null);
  const input: CustomSnoozeInput = mode === "date" ? { mode, date, time } : { mode, amount, unit };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) finish(null);
      }}
    >
      <DialogPopup className="sm:max-w-sm">
        <form
          className="flex min-h-0 flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            const until = resolveCustomSnooze(input, new Date());
            if (!until) {
              setError(
                mode === "date"
                  ? "Choose a valid date and time in the future."
                  : "Enter a positive duration.",
              );
              return;
            }
            finish({ until });
          }}
        >
          <DialogHeader>
            <DialogTitle>Custom snooze</DialogTitle>
            <DialogDescription>Choose when this thread returns to the list.</DialogDescription>
          </DialogHeader>
          <DialogPanel>
            <div className="flex flex-col gap-4">
              <ToggleGroup
                aria-label="Schedule type"
                className="w-full *:flex-1"
                value={[mode]}
                onValueChange={(next) => {
                  const value = next[0];
                  if (value === "date" || value === "duration") setMode(value);
                  setError(null);
                }}
              >
                <Toggle value="date">Date and time</Toggle>
                <Toggle value="duration">Duration</Toggle>
              </ToggleGroup>
              {mode === "date" ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* The platform's own date and time controls: they already
                      carry a picker, a keyboard path and the user's locale, and
                      the values they produce are exactly the local `YYYY-MM-DD`
                      and `HH:MM` that resolveCustomSnooze validates. */}
                  <Label className="flex min-w-0 flex-col items-stretch" htmlFor={`${id}-date`}>
                    Date
                    <Input
                      nativeInput
                      id={`${id}-date`}
                      className="h-9 sm:h-8"
                      type="date"
                      required
                      min={localSnoozeDate(new Date())}
                      value={date}
                      onChange={(event) => {
                        setDate(event.target.value);
                        setError(null);
                      }}
                    />
                  </Label>
                  <Label className="flex min-w-0 flex-col items-stretch" htmlFor={`${id}-time`}>
                    Time
                    <Input
                      nativeInput
                      id={`${id}-time`}
                      className="h-9 sm:h-8"
                      type="time"
                      required
                      value={time}
                      onChange={(event) => {
                        setTime(event.target.value);
                        setError(null);
                      }}
                    />
                  </Label>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <NumberField
                    id={`${id}-amount`}
                    min={0}
                    step="any"
                    value={amount === "" ? null : Number(amount)}
                    onValueChange={(value) => {
                      setAmount(value === null ? "" : String(value));
                      setError(null);
                    }}
                  >
                    <Label htmlFor={`${id}-amount`}>Snooze for</Label>
                    <NumberFieldGroup>
                      <NumberFieldDecrement aria-label="Decrease duration" />
                      <NumberFieldInput required />
                      <NumberFieldIncrement aria-label="Increase duration" />
                    </NumberFieldGroup>
                  </NumberField>
                  <Label className="flex min-w-0 flex-col items-stretch" htmlFor={`${id}-unit`}>
                    Unit
                    <Select
                      value={unit}
                      onValueChange={(value) => {
                        if (value === "minutes" || value === "hours" || value === "days")
                          setUnit(value);
                        setError(null);
                      }}
                    >
                      <SelectTrigger id={`${id}-unit`} className="min-w-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectPopup>
                        <SelectItem value="minutes">Minutes</SelectItem>
                        <SelectItem value="hours">Hours</SelectItem>
                        <SelectItem value="days">Days</SelectItem>
                      </SelectPopup>
                    </Select>
                  </Label>
                </div>
              )}
            </div>
            {error && (
              <p role="alert" className="text-destructive">
                {error}
              </p>
            )}
          </DialogPanel>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => finish(null)}>
              Cancel
            </Button>
            <Button type="submit">Snooze</Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
}
